<script setup lang="ts">
import { ref, reactive, computed, watch, onUnmounted, nextTick } from 'vue'
import { useIwiStore } from '@/stores/iwi'
import { useTeamsStore } from '@/stores/teams'
import { usePersonnelStore } from '@/stores/personnel'
import FosSelect from '@/components/ui/FosSelect.vue'
import CoordinateInput from '@/components/ui/CoordinateInput.vue'
import { parseCoordinates, formatCoordinates } from '@/utils/coordinates'
import type { Personnel } from '@/types'

const props = defineProps<{
  incidentId: string
  opId: string
}>()

const iwiStore = useIwiStore()
const teamsStore = useTeamsStore()
const personnelStore = usePersonnelStore()

// ─── Team context ────────────────────────────────────────────────────────────

const declaringTeam = computed(() =>
  teamsStore.teams.find((t) => t.team_id === iwiStore.activeIwi?.declared_by_team_id) ?? null,
)

// Personnel on the declaring team (active user_ops joined to roster)
const teamPersonnel = computed<Personnel[]>(() => {
  if (!declaringTeam.value) return []
  const activeOps = teamsStore.getTeamMembers(declaringTeam.value.team_id)
  return activeOps
    .map((uo) => personnelStore.roster.find((p) => p.personnel_id === uo.personnel_id))
    .filter((p): p is Personnel => p !== undefined)
})

const teamLeader = computed<Personnel | null>(() => {
  const leadId = declaringTeam.value?.team_leader_personnel_id
  if (!leadId) return null
  return personnelStore.roster.find((p) => p.personnel_id === leadId) ?? null
})

// ─── Local field state ───────────────────────────────────────────────────────

const fields = reactive({
  chief_complaint: '',
  patient_location: '',
  point_of_contact: '',
  pickup_location: '',
  patient_name: '',
  patient_age: '',
  patient_sex: '',
  patient_agency: '',
  team_number: '',
  emt_name: '',
})

const focusedField = ref<string | null>(null)

watch(
  () => iwiStore.activeIwi,
  (iwi) => {
    if (!iwi) return
    for (const key of Object.keys(fields) as (keyof typeof fields)[]) {
      if (focusedField.value === key) continue
      const stored = (iwi as Record<string, unknown>)[key] as string | null | undefined

      if (stored != null && stored !== '') {
        fields[key] = stored
      } else if (key === 'point_of_contact' && teamLeader.value) {
        const autofilled = `${teamLeader.value.first} ${teamLeader.value.last}`
        fields[key] = autofilled
        iwiStore.updateIwiField(props.incidentId, props.opId, iwi.iwi_id, key, autofilled)
      } else if (key === 'team_number' && iwi.declared_by_team_identifier) {
        fields[key] = iwi.declared_by_team_identifier
        iwiStore.updateIwiField(props.incidentId, props.opId, iwi.iwi_id, key, iwi.declared_by_team_identifier)
      } else {
        fields[key] = stored ?? ''
      }
    }
  },
  { immediate: true, deep: true },
)

// Also re-run when team leader resolves (personnel may load after IWI)
watch(teamLeader, (leader) => {
  const iwi = iwiStore.activeIwi
  if (leader && !iwi?.point_of_contact && focusedField.value !== 'point_of_contact' && iwi) {
    const autofilled = `${leader.first} ${leader.last}`
    fields.point_of_contact = autofilled
    iwiStore.updateIwiField(props.incidentId, props.opId, iwi.iwi_id, 'point_of_contact', autofilled)
  }
})

// ─── Timer ───────────────────────────────────────────────────────────────────

const iwiElapsed = ref('0:00')
let timer: ReturnType<typeof setInterval> | null = null

watch(
  () => iwiStore.activeIwi?.declared_at,
  (declaredAt) => {
    if (timer) clearInterval(timer)
    timer = null
    if (declaredAt) {
      timer = setInterval(() => {
        const elapsed = Date.now() - declaredAt.toDate().getTime()
        const totalSecs = Math.floor(elapsed / 1000)
        iwiElapsed.value = `${Math.floor(totalSecs / 60)}:${String(totalSecs % 60).padStart(2, '0')}`
      }, 1000)
    } else {
      iwiElapsed.value = '0:00'
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  if (timer) clearInterval(timer)
})

// ─── Patient name autocomplete ───────────────────────────────────────────────

const patientSuggestions = ref<Personnel[]>([])

function searchPatient(q: string) {
  if (!q.trim()) {
    patientSuggestions.value = [...teamPersonnel.value]
    return
  }
  const lower = q.toLowerCase()
  patientSuggestions.value = teamPersonnel.value.filter((p) =>
    `${p.first} ${p.last}`.toLowerCase().includes(lower),
  )
}

function selectPatient(p: Personnel) {
  fields.patient_name = `${p.first} ${p.last}`
  fields.patient_agency = p.agency ?? ''
  patientSuggestions.value = []
  focusedField.value = null
  const iwi = iwiStore.activeIwi
  if (!iwi) return
  iwiStore.updateIwiField(props.incidentId, props.opId, iwi.iwi_id, 'patient_name', fields.patient_name || null)
  iwiStore.updateIwiField(props.incidentId, props.opId, iwi.iwi_id, 'patient_agency', fields.patient_agency || null)
}

// ─── Save handlers ────────────────────────────────────────────────────────────

const inputClass = 'w-full bg-surface-container-high text-on-surface font-mono text-sm px-3 py-2 border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary/30 placeholder:text-outline/50 disabled:opacity-40'

function onFocus(field: string) {
  focusedField.value = field
  if (field === 'patient_name') {
    nextTick(() => searchPatient(fields.patient_name))
  }
}

function onBlur(field: keyof typeof fields) {
  focusedField.value = null
  patientSuggestions.value = []
  const iwi = iwiStore.activeIwi
  if (!iwi) return
  iwiStore.updateIwiField(props.incidentId, props.opId, iwi.iwi_id, field, fields[field] || null)
}

const patientLocationCoords = computed(() => parseCoordinates(fields.patient_location))
const pickupLocationCoords = computed(() => parseCoordinates(fields.pickup_location))

function onPatientLocationUpdate(coords: { lat: number | null; lng: number | null }) {
  const formatted =
    coords.lat !== null && coords.lng !== null ? formatCoordinates(coords.lat, coords.lng) : ''
  fields.patient_location = formatted
  onBlur('patient_location')
}

function onPickupLocationUpdate(coords: { lat: number | null; lng: number | null }) {
  const formatted =
    coords.lat !== null && coords.lng !== null ? formatCoordinates(coords.lat, coords.lng) : ''
  fields.pickup_location = formatted
  onBlur('pickup_location')
}

function onBooleanChange(field: string, value: string) {
  const iwi = iwiStore.activeIwi
  if (!iwi) return
  const boolVal = value === 'yes' ? true : value === 'no' ? false : null
  iwiStore.updateIwiField(props.incidentId, props.opId, iwi.iwi_id, field, boolVal)
}

function onTransportChange(value: string) {
  const iwi = iwiStore.activeIwi
  if (!iwi) return
  iwiStore.updateIwiField(props.incidentId, props.opId, iwi.iwi_id, 'transport', value || null)
}

const yesNoOptions = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
]

const transportOptions = [
  { label: 'Air', value: 'air' },
  { label: 'Ground', value: 'ground' },
  { label: 'Self', value: 'self' },
]

const genderOptions = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
  { label: 'Non-Binary', value: 'non-binary' },
  { label: 'Other', value: 'other' },
]

function onGenderChange(value: string) {
  const iwi = iwiStore.activeIwi
  if (!iwi) return
  iwiStore.updateIwiField(props.incidentId, props.opId, iwi.iwi_id, 'patient_sex', value || null)
}
</script>

<template>
  <div
    v-if="iwiStore.activeIwi"
    class="flex flex-col border-l-2 border-red-500 bg-surface overflow-y-auto"
  >
    <!-- Panel header -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-border bg-red-600/10">
      <div class="flex items-center gap-2">
        <span class="w-2 h-2 rounded-full bg-red-500 iwi-dot-pulse shrink-0" />
        <span class="font-headline font-black text-xs uppercase tracking-widest text-red-700 dark:text-red-400">
          IWI — Team {{ iwiStore.activeIwi.declared_by_team_identifier }}
        </span>
      </div>
      <span class="font-mono text-lg font-bold text-red-600 leading-none tabular-nums">{{ iwiElapsed }}</span>
    </div>

    <!-- Form -->
    <div class="p-4 flex flex-col gap-3">
      <p class="font-mono text-[0.65rem] uppercase tracking-widest text-outline border-b border-border pb-2">
        Incident Report
      </p>

      <div class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Chief Complaint</label>
        <input
          v-model="fields.chief_complaint"
          :class="inputClass"
          placeholder="Nature of emergency..."
          @focus="onFocus('chief_complaint')"
          @blur="onBlur('chief_complaint')"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Life Threatening?</label>
        <FosSelect
          :model-value="iwiStore.activeIwi.severity_life_threatening === true ? 'yes' : iwiStore.activeIwi.severity_life_threatening === false ? 'no' : undefined"
          :options="yesNoOptions"
          placeholder="Select..."
          @update:model-value="onBooleanChange('severity_life_threatening', $event)"
        />
      </div>

      <CoordinateInput
        :lat="patientLocationCoords?.lat ?? null"
        :lng="patientLocationCoords?.lng ?? null"
        label="Patient Location"
        @update:coords="onPatientLocationUpdate"
      />

      <!-- Point of Contact — auto-fills from team leader -->
      <div class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">
          Point of Contact
          <span v-if="teamLeader && !iwiStore.activeIwi.point_of_contact" class="normal-case tracking-normal text-primary ml-1">(team leader)</span>
        </label>
        <input
          v-model="fields.point_of_contact"
          :class="inputClass"
          placeholder="Team leader name/callsign..."
          @focus="onFocus('point_of_contact')"
          @blur="onBlur('point_of_contact')"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Transport</label>
        <FosSelect
          :model-value="iwiStore.activeIwi.transport ?? undefined"
          :options="transportOptions"
          placeholder="Select..."
          @update:model-value="onTransportChange($event)"
        />
      </div>

      <CoordinateInput
        :lat="pickupLocationCoords?.lat ?? null"
        :lng="pickupLocationCoords?.lng ?? null"
        label="Pickup Location"
        @update:coords="onPickupLocationUpdate"
      />

      <p class="font-mono text-[0.65rem] uppercase tracking-widest text-outline border-b border-border pb-2 mt-1">
        Patient Info
      </p>

      <!-- Patient Name — autocomplete from team members -->
      <div class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Patient Name</label>
        <div class="relative">
          <input
            v-model="fields.patient_name"
            :class="inputClass"
            placeholder="Search team members..."
            autocomplete="off"
            @focus="onFocus('patient_name'); searchPatient(fields.patient_name)"
            @input="searchPatient(fields.patient_name)"
            @blur="nextTick(() => { patientSuggestions = []; onBlur('patient_name') })"
          />
          <div
            v-if="patientSuggestions.length > 0"
            class="absolute z-50 top-full left-0 right-0 bg-surface-container-high border border-border rounded-b shadow-xl max-h-36 overflow-y-auto"
          >
            <button
              v-for="p in patientSuggestions"
              :key="p.personnel_id"
              type="button"
              class="w-full text-left px-3 py-2 font-mono text-xs text-on-surface hover:bg-surface-container-highest transition-colors"
              @mousedown.prevent="selectPatient(p)"
            >
              <span class="font-bold">{{ p.first }} {{ p.last }}</span>
              <span v-if="p.agency" class="text-outline ml-2">{{ p.agency }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-2">
        <div class="flex flex-col gap-1">
          <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Age</label>
          <input
            v-model="fields.patient_age"
            :class="inputClass"
            placeholder="Age..."
            @focus="onFocus('patient_age')"
            @blur="onBlur('patient_age')"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Gender</label>
          <FosSelect
            :model-value="iwiStore.activeIwi.patient_sex ?? undefined"
            :options="genderOptions"
            placeholder="Select..."
            @update:model-value="onGenderChange($event)"
          />
        </div>
      </div>

      <!-- Agency — auto-fills when patient selected from autocomplete -->
      <div class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Patient Agency</label>
        <input
          v-model="fields.patient_agency"
          :class="inputClass"
          placeholder="Agency..."
          @focus="onFocus('patient_agency')"
          @blur="onBlur('patient_agency')"
        />
      </div>

      <!-- Team Number — auto-fills from declaring team identifier -->
      <div class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Team Number</label>
        <input
          v-model="fields.team_number"
          :class="inputClass"
          placeholder="Team #..."
          @focus="onFocus('team_number')"
          @blur="onBlur('team_number')"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">EMT with Patient?</label>
        <FosSelect
          :model-value="iwiStore.activeIwi.emt_with_patient === true ? 'yes' : iwiStore.activeIwi.emt_with_patient === false ? 'no' : undefined"
          :options="yesNoOptions"
          placeholder="Select..."
          @update:model-value="onBooleanChange('emt_with_patient', $event)"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">EMT Name</label>
        <input
          v-model="fields.emt_name"
          :class="inputClass"
          placeholder="EMT name..."
          @focus="onFocus('emt_name')"
          @blur="onBlur('emt_name')"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.iwi-dot-pulse {
  animation: dot-pulse 1.2s ease-in-out infinite;
}

@keyframes dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.75); }
}
</style>
